import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconStudentSidebarArchive,
  iconStudentSidebarCheck,
  iconStudentSidebarDropdown,
  iconStudentSidebarQuiz,
  iconStudentSidebarVideo,
} from '../../assets';
import { useTheme } from '../../theme';

const ICON_SIZE = 16;

export type StudentCoursePlayerActivityType = 'video' | 'resource' | 'quiz';

interface LessonItem {
  id: string;
  title: string;
  duration: string;
  xp: string;
  status: 'done' | 'active' | 'locked';
  /** Ícone à esquerda conforme o tipo de atividade. Default: `video`. */
  activityType?: StudentCoursePlayerActivityType;
  /**
   * Aula em destaque no syllabus (fundo amarelo + borda esquerda).
   * Se omitido, o destaque segue `status === 'active'`.
   */
  isCurrent?: boolean;
}

interface ModuleItem {
  id: string;
  title: string;
  expanded?: boolean;
  lessons: LessonItem[];
}

export interface StudentCoursePlayerActivitiesSidebarProps {
  progressPercent: number;
  progressHint: string;
  modules: ModuleItem[];
  /** Destaque da linha no syllabus; se omitido, usa `isCurrent` / `status === 'active'`. */
  selectedLessonId?: string;
  onLessonSelect?: (lessonId: string) => void;
  aboutTitle: string;
  aboutDescription: string;
  categoryLabel: string;
  categoryValue: string;
  durationLabel: string;
  durationValue: string;
  totalXpLabel: string;
  totalXpValue: string;
}

function activityTypeIcon(
  type: StudentCoursePlayerActivityType | undefined
): string {
  switch (type ?? 'video') {
    case 'resource':
      return iconStudentSidebarArchive;
    case 'quiz':
      return iconStudentSidebarQuiz;
    default:
      return iconStudentSidebarVideo;
  }
}

export function StudentCoursePlayerActivitiesSidebar({
  progressPercent,
  progressHint,
  modules,
  selectedLessonId,
  onLessonSelect,
  aboutTitle,
  aboutDescription,
  categoryLabel,
  categoryValue,
  durationLabel,
  durationValue,
  totalXpLabel,
  totalXpValue,
}: StudentCoursePlayerActivitiesSidebarProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const boundedProgress = Math.max(0, Math.min(progressPercent, 100));

  const [expandedById, setExpandedById] = useState<Record<string, boolean>>(
    () => Object.fromEntries(modules.map(m => [m.id, m.expanded ?? false]))
  );

  /** Sincroniza quando os módulos chegam após o load (evita ficar tudo colapsado com lista inicial vazia). */
  useEffect(() => {
    setExpandedById(prev => {
      const next: Record<string, boolean> = {};
      for (const m of modules) {
        next[m.id] = prev[m.id] ?? m.expanded ?? false;
      }
      return next;
    });
  }, [modules]);

  const primary = theme.palette.primary.main;
  const sidebar = theme.palette.studentCoursePlayer.sidebar;

  return (
    <aside
      style={{
        width: 340,
        maxWidth: '100%',
        backgroundColor: theme.palette.background.paper,
        border: 'none',
        borderRadius: 0,
        padding: theme.spacing.lg,
        boxSizing: 'border-box',
        alignSelf: 'stretch',
      }}
    >
      <div style={{ marginBottom: theme.spacing.lg }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing.sm,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.palette.text.primary,
            }}
          >
            {t('student_course_player.sidebar.course_progress')}
          </h3>
          <span
            style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.palette.text.primary,
            }}
          >
            {boundedProgress}%
          </span>
        </div>
        <div
          style={{
            width: '100%',
            height: 8,
            borderRadius: theme.shape.borderRadius.full,
            overflow: 'hidden',
            backgroundColor: sidebar.progressTrackBackground,
            marginBottom: theme.spacing.sm,
          }}
        >
          <div
            style={{
              width: `${boundedProgress}%`,
              height: '100%',
              backgroundColor: primary,
            }}
          />
        </div>
        <p
          style={{
            margin: 0,
            fontSize: theme.typography.fontSize.xs,
            color: theme.palette.text.secondary,
          }}
        >
          {progressHint}
        </p>
      </div>

      <div style={{ marginBottom: theme.spacing.lg }}>
        <h3
          style={{
            margin: `0 0 ${theme.spacing.sm} 0`,
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.palette.text.primary,
          }}
        >
          {t('student_course_player.sidebar.course_syllabus')}
        </h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.sm,
          }}
        >
          {modules.map(module => {
            const isOpen = expandedById[module.id] ?? false;
            return (
              <div
                key={module.id}
                style={{
                  border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
                  borderRadius: theme.shape.borderRadius.sm,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setExpandedById(prev => ({
                      ...prev,
                      [module.id]: !isOpen,
                    }))
                  }
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing.sm,
                    padding: theme.spacing.sm2,
                    margin: 0,
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: sidebar.moduleHeaderBackground,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.palette.text.primary,
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ minWidth: 0 }}>{module.title}</span>
                  <img
                    src={iconStudentSidebarDropdown}
                    alt=""
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    aria-hidden
                    style={{
                      flexShrink: 0,
                      display: 'block',
                      objectFit: 'contain',
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </button>
                {isOpen && module.lessons.length > 0 && (
                  <div style={{ padding: 0 }}>
                    {module.lessons.map((lesson, lessonIndex) => {
                      const completed = lesson.status === 'done';
                      const highlighted =
                        selectedLessonId !== undefined
                          ? lesson.id === selectedLessonId
                          : lesson.isCurrent === true ||
                            (lesson.isCurrent === undefined &&
                              lesson.status === 'active');
                      const typeIcon = activityTypeIcon(lesson.activityType);

                      return (
                        <button
                          key={lesson.id}
                          type="button"
                          onClick={() => onLessonSelect?.(lesson.id)}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `${ICON_SIZE}px 1fr auto ${ICON_SIZE}px`,
                            alignItems: 'center',
                            columnGap: theme.spacing.sm,
                            padding: theme.spacing.sm2,
                            margin: 0,
                            borderBottom:
                              lessonIndex < module.lessons.length - 1
                                ? `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`
                                : 'none',
                            borderTop: 'none',
                            borderRight: 'none',
                            backgroundColor: highlighted
                              ? sidebar.activeRowBackground
                              : 'transparent',
                            borderLeft: highlighted
                              ? `4px solid ${primary}`
                              : '4px solid transparent',
                            boxSizing: 'border-box',
                            width: '100%',
                            cursor: onLessonSelect ? 'pointer' : 'default',
                            fontFamily: 'inherit',
                            textAlign: 'left',
                          }}
                        >
                          <img
                            src={typeIcon}
                            alt=""
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                            aria-hidden
                            style={{ display: 'block', objectFit: 'contain' }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <p
                              style={{
                                margin: 0,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: highlighted
                                  ? theme.typography.fontWeight.bold
                                  : theme.typography.fontWeight.normal,
                                color: theme.palette.text.primary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {lesson.title}
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {lesson.duration}
                            </p>
                          </div>
                          <span
                            style={{
                              fontSize: theme.typography.fontSize.xs,
                              fontWeight: theme.typography.fontWeight.bold,
                              color: sidebar.xpBadgeText,
                              backgroundColor: sidebar.xpBadgeBackground,
                              borderRadius: theme.shape.borderRadius.full,
                              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {lesson.xp}
                          </span>
                          {completed ? (
                            <img
                              src={iconStudentSidebarCheck}
                              alt=""
                              width={ICON_SIZE}
                              height={ICON_SIZE}
                              aria-hidden
                              style={{ display: 'block', objectFit: 'contain' }}
                            />
                          ) : (
                            <span
                              aria-label={lesson.status}
                              style={{
                                width: ICON_SIZE,
                                height: ICON_SIZE,
                                boxSizing: 'border-box',
                                borderRadius: theme.shape.borderRadius.full,
                                border: `${theme.shape.borderWidth.medium} solid ${theme.palette.border.input}`,
                                backgroundColor: 'transparent',
                                display: 'block',
                              }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3
          style={{
            margin: `0 0 ${theme.spacing.sm} 0`,
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.palette.text.primary,
          }}
        >
          {aboutTitle}
        </h3>
        <p
          style={{
            margin: `0 0 ${theme.spacing.md} 0`,
            color: theme.palette.text.secondary,
            fontSize: theme.typography.fontSize.sm,
          }}
        >
          {aboutDescription}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            rowGap: theme.spacing.xs2,
            columnGap: theme.spacing.md,
            fontSize: theme.typography.fontSize.sm,
          }}
        >
          <span style={{ color: theme.palette.text.secondary }}>
            {categoryLabel}
          </span>
          <span style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {categoryValue}
          </span>
          <span style={{ color: theme.palette.text.secondary }}>
            {durationLabel}
          </span>
          <span style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {durationValue}
          </span>
          <span style={{ color: theme.palette.text.secondary }}>
            {totalXpLabel}
          </span>
          <span style={{ color: primary, fontWeight: 700 }}>
            {totalXpValue}
          </span>
        </div>
      </div>
    </aside>
  );
}

export type {
  LessonItem as StudentCoursePlayerLessonItem,
  ModuleItem as StudentCoursePlayerModuleItem,
};
